import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useStore } from "react-redux";
import { getCurrentOwnerUid } from "@/utils/currentOwnerUid";
import { notifyPauseFromRehydrate, reserveSlotForRehydrated, seedPerFileProgress, watchPackageBackendStatus } from "@/Service/ChunkedUploadService";
import { notifyUploadCompleted } from "@/Service/uploadCompletionEvents";
import UploadService from "@/Service/UploadService";
import { isChunkedUploaderActive } from "@/Service/UploaderToggle";
import {
  adoptOrphanRecord,
  listOrphanPendingPackages,
  listPendingPackages,
  REHYDRATION_TRIGGER_EVENT,
  removePendingPackage,
  savePendingPackage,
  setFsHandlesForUpload,
} from "@/utils/uploadPersistence";
import { claimUploadPresence } from "@/utils/uploadOwnership";
import { uploadTelemetryService } from "@/utils/uploadTelemetry";
import {
  rehydratePausedPackage,
  setIsProgressDropdownOpen,
  setRehydrationStatus,
  surfacePostBytesProcessing,
  updateUploadingPackage,
  UploadClientStatus,
} from "@/store/packagesSlice";
import { showAlert } from "@/store/alertSlice";

// Once the backend has the bytes (READY_TO_UPLOAD_TO_PROVIDER onwards), the rest of the
// pipeline is server-side and not the user's concern — the upload is "done" from the UI's
// perspective regardless of which downstream stage it's currently in. Surfacing these as a
// PAUSED card on reload (because the IDB record outlived the UI's COMPLETED dispatch) is
// always wrong; we drop the local record silently instead.
const BACKEND_STATUSES_DONE_FROM_USER_POV = new Set([
  "READY_TO_UPLOAD_TO_PROVIDER",
  "IN_PROGRESS_UPLOAD_TO_PROVIDER",
  "READY_FOR_CAT_DV",
  "CAT_DV_INGESTED",
  "UPLOADED",
  "EDITED",
  "TAGGED",
  "PUBLISHED",
  // Held in processing, but delivered — showing a resume-upload card for it would be
  // exactly the wrong prompt.
  "ON_HOLD",
]);

// Despite the legacy name, this component renders nothing. It's a boot-time hook: any persisted
// package in IndexedDB (paused or crash-interrupted) is rehydrated into the standard upload
// progress card so the user sees the upload right where they left it. The card itself handles
// the re-select & resume flow — JS File objects don't survive a reload, so a file picker is
// unavoidable, but the UX is now consistent with the in-flight view.
export const ResumePreviousUploadBanner = () => {
  const dispatch = useDispatch();
  const store = useStore();
  // Guard against double-rehydration across re-mounts (StrictMode in dev runs effects twice).
  // We re-scan on visibility wake too, so an entry here doesn't mean "never rehydrate again" —
  // it just guards a single scan-pass from injecting the same package twice.
  const rehydratedIds = useRef(new Set());
  // Track active backend-status watchers so we don't spawn duplicates on rescans, and so we
  // can tear them down on unmount.
  const watchersRef = useRef(new Map());

  // Decide what to do with an IndexedDB record whose bytes are all locally uploaded. The
  // backend has the bytes; what's left is assembly + S3 upload + downstream pipeline. We ask
  // for the current status and branch — see also waitForBackendUploadReady in
  // ChunkedUploadService which is the in-session equivalent. Helper deliberately tolerates
  // the verify call failing (degraded backend, expired token in flight, etc.) and falls back
  // to surfacing a processing card with a watcher so the user still sees the upload.
  const surfaceBackendBoundPackage = useCallback(async (pkg) => {
    // Lift FS handles + sanitize the same way the paused-rehydration path does. Even though
    // a processing card has no resume button today, keeping the data shape consistent means
    // a future retry-after-server-failure path can use the picker handles uniformly.
    const handlesBySourceFileId = {};
    const sanitizedFiles = (pkg.files || []).map((f) => {
      if (f?.fsHandle && f.sourceFileId) {
        handlesBySourceFileId[f.sourceFileId] = f.fsHandle;
      }
      const { fsHandle, ...rest } = f || {};
      return rest;
    });
    setFsHandlesForUpload(pkg.uploadId, handlesBySourceFileId);

    let initialState = null;
    try {
      const resp = await UploadService.verifyUploadStatus(pkg.uploadId);
      initialState = resp?.data?.state || null;
    } catch (err) {
      console.warn("[ResumePreviousUploadBanner] verify status failed", { uploadId: pkg.uploadId, message: err?.message });
    }

    if (initialState === "COMPLETED") {
      // Backend is fully done. Safe to drop the persisted record without surfacing anything.
      removePendingPackage(pkg.uploadId).catch(() => {});
      return;
    }
    if (initialState === "FAILED" || initialState === "NOT_FOUND" || initialState === "CANCELLED") {
      // Surface as a FAILED card so the user sees the upload didn't actually finish, even
      // though their bytes left the browser.
      const message = initialState === "CANCELLED"
        ? "Upload was cancelled on the server."
        : "Server-side processing failed after bytes were received. Please retry.";
      dispatch(surfacePostBytesProcessing({ ...pkg, files: sanitizedFiles }));
      dispatch(updateUploadingPackage({
        uploadId: pkg.uploadId,
        status: UploadClientStatus.FAILED,
        error: message,
        statusMessage: message,
      }));
      dispatch(showAlert("error", message));
      removePendingPackage(pkg.uploadId).catch(() => {});
      return;
    }

    // RECEIVED / PROCESSING / unknown — surface as "still processing" and start a watcher.
    dispatch(surfacePostBytesProcessing({ ...pkg, files: sanitizedFiles }));
    seedPerFileProgress(pkg.uploadId, sanitizedFiles);

    if (watchersRef.current.has(pkg.uploadId)) return;
    const watcher = watchPackageBackendStatus({
      uploadId: pkg.uploadId,
      onReady: (status) => {
        dispatch(updateUploadingPackage({
          uploadId: pkg.uploadId,
          status: UploadClientStatus.COMPLETED,
          statusMessage: null,
        }));
        notifyUploadCompleted({ uploadId: pkg.uploadId, status });
        removePendingPackage(pkg.uploadId).catch(() => {});
        watchersRef.current.delete(pkg.uploadId);
      },
      onFailure: (status) => {
        const message = status === "CANCELLED"
          ? "Upload was cancelled on the server."
          : status === "REJECTED"
            ? "Upload was rejected by the server."
            : "Server-side processing failed after bytes were received. Please retry.";
        dispatch(updateUploadingPackage({
          uploadId: pkg.uploadId,
          status: UploadClientStatus.FAILED,
          error: message,
          statusMessage: message,
        }));
        dispatch(showAlert("error", message));
        removePendingPackage(pkg.uploadId).catch(() => {});
        watchersRef.current.delete(pkg.uploadId);
      },
      onTerminal: (status) => {
        if (status === "FAILED" || status === "REJECTED" || status === "CANCELLED") return; // onFailure handled it
        dispatch(updateUploadingPackage({
          uploadId: pkg.uploadId,
          status: UploadClientStatus.COMPLETED,
          statusMessage: null,
        }));
        removePendingPackage(pkg.uploadId).catch(() => {});
        watchersRef.current.delete(pkg.uploadId);
      },
    });
    watchersRef.current.set(pkg.uploadId, watcher);
  }, [dispatch]);

  const loadPending = useCallback(async ({ force = false } = {}) => {
    // Mark the initial boot rehydration as done even on the early-return paths below, so
    // consumers like InProgressUploadsPage don't sit on the spinner forever when the
    // chunked uploader is disabled or there's nothing to rehydrate. Visibility-wake rescans
    // (force=true) intentionally don't touch this flag — they happen long after the initial
    // boot and the page already has content to show.
    const markRehydrationDone = () => {
      if (!force) dispatch(setRehydrationStatus("done"));
    };
    if (!isChunkedUploaderActive()) {
      markRehydrationDone();
      return;
    }
    // Multi-tenancy scoping (2026-08-05 leak fix). Read the current uid AT
    // scan time — auth state may not have loaded on the boot pass, in which
    // case listPendingPackages returns an empty list and a subsequent
    // visibility-wake rescan (once auth has landed) picks the records up.
    // Never fall back to unscoped reads: the whole point of this filter is
    // that admin's records must NOT surface for a manager who signed in on
    // the same browser.
    const currentUid = getCurrentOwnerUid();
    if (!currentUid) {
      markRehydrationDone();
      return;
    }
    // On a forced rescan (visibility wake) clear the seen-set so packages that flipped to
    // FAILED while the tab was hidden can be re-injected. The reducer is now responsible for
    // not clobbering live UPLOADING/PAUSED packages.
    if (force) rehydratedIds.current.clear();

    // Backend-verified orphan adoption (2026-08-05 follow-up). Pre-migration
    // records with no ownerUid are hidden by listPendingPackages; the store
    // middleware adopts orphans matching fetchActiveSessions results, but
    // that covers only backend-active uploads. Packages in post-bytes server
    // processing (READY_TO_UPLOAD_TO_PROVIDER, etc.) don't appear in
    // fetchActiveSessions, yet the user should still see them — we ask the
    // backend directly via verifyUploadStatus, which is auth-scoped, so a
    // non-NOT_FOUND response for the current user's token proves ownership.
    try {
      const orphans = await listOrphanPendingPackages();
      if (orphans.length > 0) {
        for (const orphan of orphans) {
          if (!orphan?.uploadId) continue;
          try {
            const resp = await UploadService.verifyUploadStatus(orphan.uploadId);
            const state = resp?.data?.state;
            // NOT_FOUND from the auth-scoped endpoint means: the backend
            // either has no such upload OR it exists but doesn't belong to
            // this user. Either way we don't adopt.
            if (state && state !== "NOT_FOUND") {
              await adoptOrphanRecord(orphan.uploadId, currentUid);
            }
          } catch {
            /* verify failures fall through — record stays hidden */
          }
        }
      }
    } catch {
      /* orphan sweep is best-effort; hidden records stay hidden */
    }

    try {
      const list = await listPendingPackages(currentUid);
      const records = (Array.isArray(list) ? list : []).filter(
        (p) => p?.files?.length > 0
      );
      let surfacedAny = false;
      // `pkg` is `let` rather than `const` so the preInit upgrade below can rebind it to
      // the full record after re-init returns — the rest of the loop body then operates on
      // the upgraded data without every reference needing a different variable.
      for (let pkg of records) {
        // Defense-in-depth: listPendingPackages already filters by ownerUid,
        // but re-check inside the loop so a future refactor (or a mocked
        // impl in tests) can't accidentally surface another user's record.
        if (pkg.ownerUid && pkg.ownerUid !== currentUid) continue;
        if (rehydratedIds.current.has(pkg.uploadId)) continue;
        // On a forced rescan, skip packages whose Redux entry is still live (UPLOADING / PAUSED).
        // Seeding per-file progress on top of a live session would clobber real progress numbers
        // with stale IndexedDB snapshots. Boot-time scan never hits this branch because Redux
        // is empty before this effect runs.
        if (force) {
          const live = store.getState().packages.uploadingPackages.find(
            (p) => p.uploadId === pkg.uploadId
          );
          if (live && (live.status === UploadClientStatus.UPLOADING || live.status === UploadClientStatus.PAUSED)) {
            rehydratedIds.current.add(pkg.uploadId);
            continue;
          }
        }
        rehydratedIds.current.add(pkg.uploadId);
        // Claim presence AS EARLY as possible — before any backend verify or preInit HTTP
        // round-trip. The reloading original tab races a lingering duplicate's grace
        // window (see NONE_GRACE_MS in useUploadOwnership); every 100ms of delay here is
        // 100ms closer to the duplicate misreading the lock release as a real orphan and
        // flashing Resume. The claim itself internally checks for an existing session lock
        // and no-ops if the original tab is still actively uploading, so calling it here
        // for a duplicated-tab mount doesn't steal presence from the true main.
        claimUploadPresence(pkg.uploadId).catch(() => { /* best-effort */ });
        // Pre-init upgrade. If this record is a preInit stub written by
        // ChunkedUploadService.upload before the backend init call returned (closes the
        // race where a fast reload between dispatch and savePendingPackage leaves the server
        // with a PENDING_CHUNKS row that the client has no trace of), call /packages with
        // the stored init body to fetch sourceFileIds and turn it into a normal full record.
        // The backend init endpoint is idempotent for resumable statuses: same uploadId
        // returns the existing UploadMetadata. On any failure we drop the stub — either the
        // backend never created the row (so there's nothing to recover) or it's in a
        // non-resumable status (the FAILED/CANCELLED branch below will surface it on next
        // reload via the regular verify path).
        if (pkg.preInit && pkg.preInitRequest) {
          try {
            const reinitResp = await UploadService.reinitPackage(pkg.preInitRequest);
            const expectedFiles = reinitResp?.data?.expectedFiles || [];
            if (expectedFiles.length === 0) {
              throw new Error("Re-init returned no expectedFiles");
            }
            const upgradedFiles = expectedFiles.map((expected) => {
              const stubFile = (pkg.files || []).find((f) => f.originalName === expected.originalName);
              return {
                sourceFileId: expected.sourceFileId,
                originalName: expected.originalName,
                sizeBytes: expected.sizeBytesExpected,
                fingerprint: stubFile?.fingerprint || null,
                fsHandle: stubFile?.fsHandle || null,
                tusEndpoint: expected.tusEndpoint,
                tusUploadUrl: expected.tusUploadUrl || null,
              };
            });
            pkg = {
              ...pkg,
              packageName: reinitResp?.data?.packageName || pkg.packageName,
              files: upgradedFiles,
              preInit: false,
              preInitRequest: null,
            };
            try {
              await savePendingPackage(pkg);
            } catch (saveErr) {
              console.warn("[ResumePreviousUploadBanner] preInit upgrade save failed", saveErr?.message);
            }
          } catch (err) {
            console.warn("[ResumePreviousUploadBanner] preInit re-init failed; dropping stub", {
              uploadId: pkg.uploadId,
              status: err?.response?.status,
              message: err?.message,
            });
            removePendingPackage(pkg.uploadId).catch(() => {});
            continue;
          }
        }
        // Pre-flight verify against the backend before deciding how to surface the record.
        // The local allFilesUploaded heuristic below relies on chunkStatus="UPLOADED" being
        // written to IndexedDB for every file via markFileUploaded — but that path does a
        // read-modify-write across separate IDB transactions, so when files complete close
        // in time the later write can clobber the earlier one and a fast/small package ends
        // up with some files un-marked. Combined with the IDB record outliving the UI's
        // COMPLETED dispatch (the record is only deleted when the backend reaches PUBLISHED
        // / UPLOADED / CAT_DV_INGESTED, while the UI completes at the earlier "ready"
        // status), the user sees finished uploads resurface as PAUSED cards on every reload.
        // Asking the backend up front — and treating any READY_TO_UPLOAD_TO_PROVIDER-or-
        // later status as "done from the user's POV" — lets us drop those stale records
        // regardless of what the local heuristic thinks.
        let backendOriginalStatus = null;
        let backendState = null;
        try {
          const resp = await UploadService.verifyUploadStatus(pkg.uploadId);
          backendOriginalStatus = resp?.data?.originalStatus || null;
          backendState = resp?.data?.state || null;
        } catch (err) {
          console.warn("[ResumePreviousUploadBanner] preflight verify failed", {
            uploadId: pkg.uploadId,
            message: err?.message,
          });
        }
        if (backendOriginalStatus && BACKEND_STATUSES_DONE_FROM_USER_POV.has(backendOriginalStatus)) {
          removePendingPackage(pkg.uploadId).catch(() => {});
          continue;
        }
        // Terminal failure path: a stuck-failed package, an admin-cancelled one, or a row that
        // never landed in the DB. Surfacing these as a paused-Resume card was the bug that
        // produced the "click Resume → 409 from POST /api/v1/upload/tus/ → both uploads
        // disappear from the UI" loop reported on dev: the validateForCreate guard correctly
        // rejects POSTs to non-RESUMABLE packages, but the user had no idea why and the IDB
        // record persisted so every reload re-surfaced the same trap. Show a FAILED card with
        // a Cancel action instead so the user has a clear exit.
        if (backendState === "FAILED" || backendState === "CANCELLED" || backendState === "NOT_FOUND") {
          const message = backendState === "CANCELLED"
            ? "Upload was cancelled on the server."
            : backendState === "NOT_FOUND"
              ? "Upload session is no longer available on the server. Please start a new upload."
              : `Upload failed on the server (status=${backendOriginalStatus || "unknown"}). Please start a new upload.`;
          const handlesBySourceFileId = {};
          const sanitizedFiles = (pkg.files || []).map((f) => {
            if (f?.fsHandle && f.sourceFileId) {
              handlesBySourceFileId[f.sourceFileId] = f.fsHandle;
            }
            const { fsHandle, ...rest } = f || {};
            return rest;
          });
          setFsHandlesForUpload(pkg.uploadId, handlesBySourceFileId);
          dispatch(surfacePostBytesProcessing({ ...pkg, files: sanitizedFiles }));
          dispatch(updateUploadingPackage({
            uploadId: pkg.uploadId,
            status: UploadClientStatus.FAILED,
            error: message,
            statusMessage: message,
          }));
          dispatch(showAlert("error", message));
          removePendingPackage(pkg.uploadId).catch(() => {});
          surfacedAny = true;
          continue;
        }
        // If every file in this record is already locally uploaded, the bytes are server-side
        // but the backend's final verdict isn't recorded yet. Don't just evict (the old
        // "zombie" path silently dropped uploads that ended up FAILED or stuck server-side —
        // they disappeared from the UI on reload). Instead, ask the backend for its current
        // status and surface accordingly: still processing → "Server is finalizing" card with
        // a live watcher; truly done → evict; failed → surfaced as a FAILED card with retry.
        //
        // Only trust the chunkStatus mark here — see the matching comment on
        // isFileAlreadyUploaded in ChunkedUploadService.js for why the bytes-based fallback
        // (bytesUploaded >= sizeBytes) is unsafe. The pre-flight verify above already drops
        // genuinely-completed packages, so the only consumer of this branch is the
        // "Server is finalizing" surface for rows that really have all chunkStatus="UPLOADED".
        const allFilesUploaded = (pkg.files || []).every((f) => f?.chunkStatus === "UPLOADED");
        if (allFilesUploaded) {
          await surfaceBackendBoundPackage(pkg);
          surfacedAny = true;
          continue;
        }
        const totalBytes = pkg.totalSize || 0;
        const uploadedBytes = (pkg.files || []).reduce(
          (acc, f) => acc + (f.bytesUploaded || 0),
          0
        );
        // Lift FS Access handles out of the Redux-bound record — FileSystemFileHandle is not
        // serializable, and storing it in state warns / breaks freezing. The handles live in
        // a module-scoped Map keyed by uploadId+sourceFileId; the card reads them by id.
        const handlesBySourceFileId = {};
        const sanitizedFiles = (pkg.files || []).map((f) => {
          if (f?.fsHandle && f.sourceFileId) {
            handlesBySourceFileId[f.sourceFileId] = f.fsHandle;
          }
          const { fsHandle, ...rest } = f || {};
          return rest;
        });
        setFsHandlesForUpload(pkg.uploadId, handlesBySourceFileId);
        // Reserve the package's queue position before any new uploads can start — keeps a
        // rehydrated paused package at the head of the queue so a newly-added upload waits
        // behind it instead of racing past while the user hasn't yet provided files via the
        // picker. The reservation is consumed when the user resumes this package (the resume
        // path's acquireUploadSlot deletes its own reservation), or by removePendingPackage
        // when the user cancels.
        reserveSlotForRehydrated(pkg.uploadId);
        uploadTelemetryService.seedPaused(pkg.uploadId, { totalBytes, uploadedBytes });
        seedPerFileProgress(pkg.uploadId, sanitizedFiles);
        // Reload is treated as a pause boundary: even if persisted FS Access handles are
        // available, do NOT silently resume. The card surfaces with explicit Resume / Pick
        // folder / Pick files controls so the user opts in. Auto-continuing after a reload
        // would surprise users who reload precisely to stop an in-flight upload.
        dispatch(rehydratePausedPackage({ ...pkg, files: sanitizedFiles }));
        // Tell the backend that this upload is paused. Without this, a package whose tab
        // got closed/reloaded mid-upload stays at CHUNK_UPLOAD_IN_PROGRESS in the DB until
        // the daily TTL sweep marks it FAILED. Idempotent on the server, fire-and-forget
        // on the client; safe to call even for records that were explicitly paused before
        // reload, because the backend treats PAUSED→PAUSED as a no-op.
        notifyPauseFromRehydrate(pkg.uploadId);
        surfacedAny = true;
      }
      if (surfacedAny) {
        // Surface the card immediately so the user notices their upload is still waiting —
        // mirrors the dropdown-open behavior when a fresh upload starts.
        dispatch(setIsProgressDropdownOpen(true));
      }
    } catch {
      // Persistence errors are non-fatal; the user can still start a fresh upload.
    } finally {
      markRehydrationDone();
    }
  }, [dispatch]);

  useEffect(() => {
    loadPending();
    // Tear down any in-flight watchers on unmount so the SSE/poll loops don't outlive the
    // banner — covers fast-refresh + actual route changes.
    const watchers = watchersRef.current;
    return () => {
      watchers.forEach((w) => { try { w?.close?.(); } catch {} });
      watchers.clear();
    };
  }, [loadPending]);

  // Re-run the rehydration scan when a card detects that the tab that owned an upload has
  // died (Web Lock released → ownership resolves to "none"). Without this, the takeover UI
  // would show a Resume button in the surviving tab but the click would no-op because the
  // stale broadcast-only record has no rehydrated=true / pausedRecord / FS handles.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onRequest = () => loadPending({ force: true });
    window.addEventListener(REHYDRATION_TRIGGER_EVENT, onRequest);
    return () => window.removeEventListener(REHYDRATION_TRIGGER_EVENT, onRequest);
  }, [loadPending]);

  // Re-run the rehydration scan when the tab becomes visible after a hidden stretch — covers
  // the laptop-sleep case: the in-flight TUS retry tail can exhaust mid-suspension and flip the
  // package to FAILED in Redux, which removes it from the in-progress page. The IndexedDB
  // record is still alive, so re-scanning surfaces the upload back as PAUSED without forcing
  // the user to reload the tab.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      // Skip quick tab-switches; those don't kill in-flight uploads, so a rescan would be
      // pointless overhead and could race the live session.
      if (!hiddenAt || Date.now() - hiddenAt < 30_000) return;
      hiddenAt = 0;
      loadPending({ force: true });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [loadPending]);

  return null;
};
